<template>
  <HrPageLayout
    title="My Leave Balance"
    subtitle="My Leave"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'Leave Balance' }
    ]"
  >
          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading leave balance...</p>
          </div>

          <div v-else-if="balanceData">
            <!-- Employee Info & Year Selector -->
            <div class="row">
              <div class="col-12">
                <div class="card bg-primary bg-gradient text-white">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h5 class="text-white mb-2">{{ balanceData.employee.fullName }}</h5>
                        <p class="text-white-50 mb-0">
                          <i class="mdi mdi-badge-account me-1"></i> {{ balanceData.employee.empNo }}
                          <span class="mx-2">|</span>
                          <i class="mdi mdi-office-building me-1"></i> {{ balanceData.employee.department }}
                        </p>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <label class="text-white-50 small d-block">Select Year</label>
                        <select v-model="selectedYear" @change="loadBalanceData" class="form-select form-select-sm">
                          <option :value="currentYear">{{ currentYear }}</option>
                          <option :value="currentYear - 1">{{ currentYear - 1 }}</option>
                          <option :value="currentYear - 2">{{ currentYear - 2 }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Summary Cards -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Total Allocated</h6>
                        <h3 class="mb-0">{{ balanceData.totals.allocated }}</h3>
                        <small class="text-muted">days</small>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-3">
                          <i class="mdi mdi-calendar-multiple font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Used</h6>
                        <h3 class="mb-0">{{ balanceData.totals.used }}</h3>
                        <small class="text-muted">days</small>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-3">
                          <i class="mdi mdi-calendar-check font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted">{{ balanceData.totals.percentageUsed }}% utilized</small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Remaining</h6>
                        <h3 class="mb-0">{{ balanceData.totals.remaining }}</h3>
                        <small class="text-muted">days</small>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-success-subtle text-success rounded-3">
                          <i class="mdi mdi-calendar-blank font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Pending Requests</h6>
                        <h3 class="mb-0">{{ balanceData.requestsSummary.pending }}</h3>
                        <small class="text-muted">{{ balanceData.requestsSummary.totalDaysPending }} days</small>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-info-subtle text-info rounded-3">
                          <i class="mdi mdi-clock-outline font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Overview Chart and Balance Breakdown -->
            <div class="row">
              <!-- Balance Overview Chart -->
              <div class="col-xl-5">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-chart-donut text-primary me-2"></i>
                      Balance Overview
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="text-center mb-3">
                      <div class="donut-chart">
                        <svg viewBox="0 0 200 200" width="200" height="200">
                          <!-- Used segment -->
                          <circle
                              cx="100"
                              cy="100"
                              r="80"
                              fill="transparent"
                              :stroke="getChartColor('used')"
                              stroke-width="40"
                              :stroke-dasharray="getCircleDashArray('used')"
                              transform="rotate(-90 100 100)"
                          />
                          <!-- Remaining segment -->
                          <circle
                              cx="100"
                              cy="100"
                              r="80"
                              fill="transparent"
                              :stroke="getChartColor('remaining')"
                              stroke-width="40"
                              :stroke-dasharray="getCircleDashArray('remaining')"
                              :stroke-dashoffset="getCircleDashOffset('used')"
                              transform="rotate(-90 100 100)"
                          />
                          <!-- Center text -->
                          <text x="100" y="95" text-anchor="middle" font-size="24" font-weight="bold" fill="#059669">
                            {{ balanceData.totals.percentageUsed }}%
                          </text>
                          <text x="100" y="115" text-anchor="middle" font-size="12" fill="#6c757d">
                            Used
                          </text>
                        </svg>
                      </div>
                    </div>
                    <div class="row text-center">
                      <div class="col-6">
                        <div class="p-2">
                          <div class="d-flex align-items-center justify-content-center mb-2">
                            <span class="legend-box bg-warning me-2"></span>
                            <span class="text-muted">Used</span>
                          </div>
                          <h4 class="mb-0">{{ balanceData.totals.used }}</h4>
                          <small class="text-muted">days</small>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="p-2">
                          <div class="d-flex align-items-center justify-content-center mb-2">
                            <span class="legend-box bg-success me-2"></span>
                            <span class="text-muted">Remaining</span>
                          </div>
                          <h4 class="mb-0">{{ balanceData.totals.remaining }}</h4>
                          <small class="text-muted">days</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Request Summary -->
              <div class="col-xl-7">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-chart-bar text-primary me-2"></i>
                      Request Summary
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-3 col-6 mb-3">
                        <div class="text-center p-3 border rounded">
                          <h3 class="text-primary mb-1">{{ balanceData.requestsSummary.total }}</h3>
                          <p class="text-muted mb-0 small">Total Requests</p>
                        </div>
                      </div>
                      <div class="col-md-3 col-6 mb-3">
                        <div class="text-center p-3 border rounded">
                          <h3 class="text-success mb-1">{{ balanceData.requestsSummary.approved }}</h3>
                          <p class="text-muted mb-0 small">Approved</p>
                          <small class="text-success">{{ balanceData.requestsSummary.totalDaysApproved }} days</small>
                        </div>
                      </div>
                      <div class="col-md-3 col-6 mb-3">
                        <div class="text-center p-3 border rounded">
                          <h3 class="text-warning mb-1">{{ balanceData.requestsSummary.pending }}</h3>
                          <p class="text-muted mb-0 small">Pending</p>
                          <small class="text-warning">{{ balanceData.requestsSummary.totalDaysPending }} days</small>
                        </div>
                      </div>
                      <div class="col-md-3 col-6 mb-3">
                        <div class="text-center p-3 border rounded">
                          <h3 class="text-danger mb-1">{{ balanceData.requestsSummary.rejected }}</h3>
                          <p class="text-muted mb-0 small">Rejected</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Detailed Balance Breakdown -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-format-list-bulleted text-primary me-2"></i>
                      Leave Balance Breakdown
                    </h5>
                    <button @click="gotTo('employee-leave-apply')" class="btn btn-primary btn-sm">
                      <i class="mdi mdi-plus me-1"></i>
                      Apply for Leave
                    </button>
                  </div>
                  <div class="card-body">
                    <div v-if="balanceData.balances.length === 0" class="text-center py-5">
                      <i class="mdi mdi-information-outline text-muted font-size-48"></i>
                      <p class="text-muted mt-2">No leave balance information available</p>
                    </div>
                    <div v-else class="table-responsive">
                      <table class="table table-hover">
                        <thead class="table-light">
                        <tr>
                          <th>Leave Type</th>
                          <th class="text-center">Allocated</th>
                          <th class="text-center">Used</th>
                          <th class="text-center">Remaining</th>
                          <th>Usage</th>
                          <th class="text-center">Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="balance in balanceData.balances" :key="balance.leaveTypeID">
                          <td>
                            <div>
                              <strong>{{ balance.leaveTypeName }}</strong>
                              <br>
                              <small class="text-muted">{{ balance.description }}</small>
                            </div>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-light text-dark">{{ balance.totalDays }} days</span>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-warning">{{ balance.usedDays }} days</span>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-success">{{ balance.remainingDays }} days</span>
                          </td>
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="progress flex-grow-1 me-2" style="height: 20px;">
                                <div
                                    class="progress-bar"
                                    :class="getProgressBarClass(balance.percentageUsed)"
                                    :style="{ width: balance.percentageUsed + '%' }">
                                  {{ balance.percentageUsed }}%
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="text-center">
                              <span v-if="balance.remainingDays > 5" class="badge bg-success">
                                <i class="mdi mdi-check-circle"></i> Good
                              </span>
                            <span v-else-if="balance.remainingDays > 0" class="badge bg-warning">
                                <i class="mdi mdi-alert"></i> Low
                              </span>
                            <span v-else class="badge bg-danger">
                                <i class="mdi mdi-close-circle"></i> Depleted
                              </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Upcoming Leaves -->
            <div class="row" v-if="balanceData.upcomingLeaves.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-calendar-clock text-primary me-2"></i>
                      Upcoming Approved Leaves
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead class="table-light">
                        <tr>
                          <th>Leave Type</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th class="text-center">Days</th>
                          <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="leave in balanceData.upcomingLeaves" :key="leave.id">
                          <td>{{ leave.leaveTypeName }}</td>
                          <td>{{ formatDate(leave.startDate) }}</td>
                          <td>{{ formatDate(leave.endDate) }}</td>
                          <td class="text-center">
                            <span class="badge bg-info">{{ leave.totalDays }} days</span>
                          </td>
                          <td>
                              <span class="badge bg-success">
                                <i class="mdi mdi-check-circle me-1"></i>Approved
                              </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">Need to apply for leave?</h6>
                        <p class="text-muted mb-0">Check your balance and submit a request</p>
                      </div>
                      <div class="d-flex gap-2">
                        <button @click="gotTo('employee-leave-apply')" class="btn btn-primary">
                          <i class="mdi mdi-plus me-1"></i>
                          Apply for Leave
                        </button>
                        <button @click="gotTo('employee-leave-history')" class="btn btn-outline-primary">
                          <i class="mdi mdi-history me-1"></i>
                          View History
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeLeaveBalance",
  components: { HrPageLayout },

  data() {
    return {
      loading: false,
      balanceData: null,
      currentYear: new Date().getFullYear(),
      selectedYear: new Date().getFullYear()
    };
  },

  mounted() {
    this.loadBalanceData();
  },

  methods: {
    async loadBalanceData() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeLeaveBalanceDetailed");
        data.append("year", this.selectedYear);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.balanceData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load leave balance');
        }
      } catch (error) {
        this.$toast?.error("Failed to load leave balance: " + error.message) ||
        console.error("Failed to load leave balance:", error);
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    getProgressBarClass(percentage) {
      if (percentage >= 80) return 'bg-danger';
      if (percentage >= 50) return 'bg-warning';
      return 'bg-success';
    },

    getChartColor(segment) {
      if (segment === 'used') return '#ffc107'; // warning
      if (segment === 'remaining') return '#28a745'; // success
      return '#6c757d'; // secondary
    },

    getCircleDashArray(segment) {
      const circumference = 2 * Math.PI * 80; // 80 is the radius

      if (segment === 'used') {
        const percentage = this.balanceData.totals.percentageUsed;
        const usedLength = (percentage / 100) * circumference;
        return `${usedLength} ${circumference}`;
      }

      if (segment === 'remaining') {
        const percentage = 100 - this.balanceData.totals.percentageUsed;
        const remainingLength = (percentage / 100) * circumference;
        return `${remainingLength} ${circumference}`;
      }

      return `0 ${circumference}`;
    },

    getCircleDashOffset(segment) {
      const circumference = 2 * Math.PI * 80;

      if (segment === 'used') {
        const percentage = this.balanceData.totals.percentageUsed;
        const usedLength = (percentage / 100) * circumference;
        return -usedLength;
      }

      return 0;
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      if (typeof route === "string" && route.startsWith("/")) {
        this.$router.push(route);
      } else {
        this.$router.push({ name: route });
      }
    }
  }
};
</script>

<style scoped>
.card-stats {
  transition: all 0.3s ease;
}

.card-stats:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.donut-chart {
  display: flex;
  justify-content: center;
  align-items: center;
}

.legend-box {
  width: 12px;
  height: 12px;
  display: inline-block;
  border-radius: 2px;
}

.progress {
  background-color: #e9ecef;
}

.table thead th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.table tbody tr:hover {
  background-color: rgba(91, 115, 232, 0.05);
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>